import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Heart,
  MoreVertical,
  Copy,
  Trash2,
  Edit,
  FolderOpen,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { PostTemplate } from '@/types/templates';

interface TemplateCardProps {
  template: PostTemplate;
  onEdit?: (template: PostTemplate) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onRemix?: (template: PostTemplate) => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  hook: '#eb5757',
  body: '#2383e2',
  cta: '#4dab5a',
  hashtags: '#9b51e0',
  first_comment: '#dfab01',
  full: '#5b9bd5',
};

const CATEGORY_LABELS: Record<string, string> = {
  hook: 'templates_category_hooks',
  body: 'templates_category_body',
  cta: 'templates_category_cta',
  hashtags: 'templates_category_hashtags',
  first_comment: 'templates_category_first_comment',
  full: 'templates_category_full',
};

const PLATFORM_ICONS: Record<string, string> = {
  twitter: '𝕏',
  facebook: 'f',
  instagram: '📷',
  linkedin: 'in',
  tiktok: '♪',
  youtube: '▶',
  pinterest: 'P',
  bluesky: '☁',
};

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onEdit: _onEdit,
  onDuplicate,
  onDelete,
  onToggleFavorite,
  onRemix,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [isHeartAnimating, setIsHeartAnimating] = useState(false);

  const categoryColor = CATEGORY_COLORS[template.category] || CATEGORY_COLORS.full;
  const categoryLabel = t(
    CATEGORY_LABELS[template.category] || 'templates_category_full',
    template.category.charAt(0).toUpperCase() + template.category.slice(1)
  );

  const platforms = Object.keys(template.platform_variants || {});
  const contentLines = template.content.split('\n').slice(0, 2);
  const performancePercentage = Math.min(
    Math.max(template.performance_score || 0, 0),
    100
  );

  const handleFavoriteClick = () => {
    setIsHeartAnimating(true);
    onToggleFavorite(template.id);
    setTimeout(() => setIsHeartAnimating(false), 500);
  };

  return (
    <div className="group relative bg-n-card border border-n-border rounded-lg transition-colors duration-200 hover:bg-n-hover flex flex-col h-full">
      {/* Header Section with Category Dot and Title */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-start justify-between gap-3 mb-2">
          {/* Category Dot + Label + Title */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              {/* Category Dot */}
              <div
                className="h-2 w-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: categoryColor }}
                title={categoryLabel}
              />
              <span className="text-xs font-semibold text-n-text-lt uppercase tracking-wide">
                {categoryLabel}
              </span>
            </div>
            <h3 className="font-semibold text-n-text text-sm line-clamp-2 leading-tight">
              {template.name}
            </h3>
          </div>

          {/* Favorite + Menu Buttons */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {template.is_ai_generated && (
              <div className="flex items-center gap-0.5 px-2 py-1 rounded text-xs font-semibold bg-n-hover text-n-text-md">
                <Sparkles size={12} />
                {t('templates_ai_short', 'AI')}
              </div>
            )}
            <button
              onClick={handleFavoriteClick}
              className={`p-1 text-n-text-lt hover:text-n-text-md hover:bg-n-card rounded border border-n-border transition-all duration-200 ${
                isHeartAnimating ? 'scale-125' : 'scale-100'
              }`}
              title={
                template.is_favorite
                  ? t('templates_remove_favorite', 'Remove from favorites')
                  : t('templates_add_favorite', 'Add to favorites')
              }
              aria-label={
                template.is_favorite
                  ? t('templates_remove_favorite', 'Remove from favorites')
                  : t('templates_add_favorite', 'Add to favorites')
              }
            >
              <Heart
                size={16}
                className={`transition-all duration-200 ${
                  template.is_favorite ? 'text-n-red fill-n-red' : ''
                }`}
              />
            </button>
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1 text-n-text-lt hover:text-n-text-md hover:bg-n-card rounded border border-n-border transition-colors"
                aria-label={t('templates_action_menu', 'More actions')}
                aria-expanded={showMenu}
              >
                <MoreVertical size={16} />
              </button>

              {/* Dropdown Menu */}
              {showMenu && (
                <div
                  className="absolute right-0 top-full mt-1 w-48 bg-n-card border border-n-border rounded-n-sm py-1 shadow-n z-50 overflow-hidden"
                  role="menu"
                >
                  <button
                    onClick={() => {
                      navigate(`/templates/${template.id}/edit`);
                      setShowMenu(false);
                    }}
                    className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-text-md hover:bg-n-hover transition-colors text-left"
                    role="menuitem"
                  >
                    <Edit size={16} />
                    {t('templates_action_edit', 'Edit')}
                  </button>
                  <button
                    onClick={() => {
                      onDuplicate(template.id);
                      setShowMenu(false);
                    }}
                    className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-text-md hover:bg-n-hover transition-colors text-left"
                    role="menuitem"
                  >
                    <Copy size={16} />
                    {t('templates_action_duplicate', 'Duplicate')}
                  </button>
                  {onRemix && (
                    <button
                      onClick={() => {
                        onRemix(template);
                        setShowMenu(false);
                      }}
                      className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-text-md hover:bg-n-hover transition-colors text-left"
                      role="menuitem"
                    >
                      <Sparkles size={16} />
                      {t('templates_action_remix', 'Remix')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowMenu(false);
                    }}
                    className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-text-md hover:bg-n-hover transition-colors text-left"
                    role="menuitem"
                  >
                    <FolderOpen size={16} />
                    {t('templates_action_move', 'Move to folder')}
                  </button>
                  <div className="h-px bg-n-border" />
                  <button
                    onClick={() => {
                      onDelete(template.id);
                      setShowMenu(false);
                    }}
                    className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-red hover:bg-n-red-bg transition-colors text-left"
                    role="menuitem"
                  >
                    <Trash2 size={16} />
                    {t('templates_action_delete', 'Delete')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content Preview */}
      <div className="px-4 py-2 flex-1 min-h-0">
        <div className="space-y-0.5 text-xs text-n-text-md line-clamp-3 overflow-hidden">
          {contentLines.length > 0 ? (
            contentLines.map((line, idx) => (
              <p key={idx} className="line-clamp-1">
                {line || '\u00A0'}
              </p>
            ))
          ) : (
            <p className="italic text-n-text-lt">
              {t('templates_empty_content', '(No content)')}
            </p>
          )}
        </div>
      </div>

      {/* Platform Chips */}
      {platforms.length > 0 && (
        <div className="px-4 py-2 border-t border-n-border">
          <div className="flex items-center gap-1.5 flex-wrap">
            {platforms.slice(0, 3).map((platform) => (
              <span
                key={platform}
                className="text-[10px] px-1.5 py-0.5 rounded bg-n-hover text-n-text-md font-medium"
              >
                {PLATFORM_ICONS[platform.toLowerCase()] || platform[0].toUpperCase()}
                {platform.length > 1 && ' ' + platform.substring(1).toLowerCase()}
              </span>
            ))}
            {platforms.length > 3 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-n-hover text-n-text-md font-medium">
                +{platforms.length - 3}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Stats Row */}
      <div className="px-4 py-2 border-t border-n-border">
        <div className="flex items-center justify-between text-xs text-n-text-md gap-2">
          <span>
            {t('templates_used_count', '{{count}} uses', {
              count: template.usage_count || 0,
            })}
          </span>
          <div className="flex items-center gap-1">
            <TrendingUp size={12} />
            <span>
              {t('templates_avg_reach', '~{{reach}}', {
                reach:
                  template.avg_reach && template.avg_reach > 0
                    ? (template.avg_reach / 1000).toFixed(1) + 'k'
                    : '0',
              })}
            </span>
          </div>
          {template.performance_score > 0 && (
            <span className="text-xs text-n-text-md font-semibold">
              {Math.round(performancePercentage)}%
            </span>
          )}
        </div>
      </div>

    </div>
  );
};

export default TemplateCard;
